import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { notify } from '@/lib/notify';

interface CopyButtonProps {
  value: string;
  label?: string;
  size?: 'sm' | 'default';
}

/** Кнопка копирования текста в буфер обмена с кратким подтверждением. */
export function CopyButton({ value, label = 'Копировать', size = 'sm' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!navigator.clipboard) {
      notify.error('Буфер обмена недоступен в этом браузере');
      return;
    }
    void navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        notify.success('Скопировано');
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => notify.error('Не удалось скопировать'),
    );
  };

  return (
    <Button type="button" variant="outline" size={size} onClick={handleCopy} className="gap-1.5">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label}
    </Button>
  );
}

interface CodeSnippetProps {
  title: string;
  value: string;
  hint?: string;
}

/** Блок кода с заголовком и кнопкой копирования. */
export function CodeSnippet({ title, value, hint }: CodeSnippetProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{title}</span>
        <CopyButton value={value} />
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/50 p-3 font-mono text-xs">
        {value}
      </pre>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
